import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useApi from '../Utils/useApi';
import useCoreApi from '../Utils/useCoreApi';
import CssBaseline from '@mui/material/CssBaseline';
import GlobalStyles from '@mui/material/GlobalStyles';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Grid from '@mui/material/Grid';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import UserGreeting from './UserGreeting';
import { createTheme, ThemeProvider } from '@mui/material/styles';


const MyAccount = () => {
  const navigate = useNavigate();
  const api = useApi();
  const coreApi = useCoreApi();
  const [newPassword, setNewPassword] = useState('');
  const [receiveWeeklyReport, setReceiveWeeklyReport] = useState(true);
  const [acceptMarketingEmails, setAcceptMarketingEmails] = useState(true);
  const defaultTheme = createTheme();
  const [confirmPassword, setConfirmPassword] = useState('');
  const [updateSuccess, setUpdateSuccess] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [emailPassword, setEmailPassword] = useState('');
  const [emailUpdateSuccess, setEmailUpdateSuccess] = useState(false);
  const [emailUpdateError, setEmailUpdateError] = useState('');

  useEffect(() => {
    const accessToken = localStorage.getItem('accessToken');
    const userId = localStorage.getItem('user_id'); // Make sure the user ID is stored in local storage or retrieved from the accessToken
  
    if (!accessToken || !userId) {
      console.log("Missing authentication details");
      return;
    }
  
  
    // Fetch user preferences
    api.get(`/user/user_preferences/${userId}`, {
      headers: {
        'Authorization': `Bearer ${accessToken}`
      }
    })
    .then(response => {
      const { receive_weekly_report, accept_marketing_emails } = response.data;
      setReceiveWeeklyReport(receive_weekly_report);
      setAcceptMarketingEmails(accept_marketing_emails);
    })
    .catch(error => console.log("Error fetching user preferences:", error));
  
  }, []);
  
  const handleSubmit = (event) => {
    event.preventDefault();
  
    // Ensure passwords match before submitting
    if (newPassword !== confirmPassword) {
      alert('Passwords do not match!');
      return;
    }
  
    // Update password if newPassword is not empty
    if (newPassword) {
      updatePassword(newPassword);
    }

  };

  const updatePassword = (newPassword) => {
    const accessToken = localStorage.getItem('accessToken');
    const userId = localStorage.getItem('user_id');
  
    if (!accessToken || !userId) {
      console.log("Missing authentication details");
      return;
    }
  
    // Assuming you have a backend endpoint '/user/change_password' for updating the password
    api.post(`/user/parent/change_password`, { password: newPassword }, {
      headers: {
        'Authorization': `Bearer ${accessToken}`
      }
    })
    .then(response => {
      console.log("Password updated successfully");
      setNewPassword('');
      setConfirmPassword('');
      setUpdateSuccess(true); // Set the success state to true
      // Optionally, reset the success message after a few seconds
      setTimeout(() => setUpdateSuccess(false), 5000);
    })
    .catch(error => {
      console.error("Error updating password:", error);
      // Handle the error...
    });
  };

  
  const handleContactClick = () => {
    navigate('/contactus');  // Assuming the route for Contact Us page is '/contact'
  };

  const handleEmailUpdate = async () => {
    // Clear previous messages
    setEmailUpdateError('');
    setEmailUpdateSuccess(false);

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newEmail)) {
      setEmailUpdateError('Please enter a valid email address');
      return;
    }

    if (!emailPassword) {
      setEmailUpdateError('Please enter your current password');
      return;
    }

    const accessToken = localStorage.getItem('accessToken');

    if (!accessToken) {
      setEmailUpdateError('Authentication required. Please log in again.');
      return;
    }

    try {
      console.log('Attempting to update email...');
      const response = await coreApi.put('/user/parent/update_email',
        {
          currentPassword: emailPassword,
          newEmail: newEmail
        },
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`
          }
        }
      );

      console.log('Email updated successfully');
      setEmailUpdateSuccess(true);
      setNewEmail('');
      setEmailPassword('');

      // Optionally, reset the success message after a few seconds
      setTimeout(() => setEmailUpdateSuccess(false), 5000);
    } catch (error) {
      console.error('Error updating email:', error.response?.status, error.response?.data);
      const errorMessage = error.response?.data?.error || 'Failed to update email. Please try again.';
      setEmailUpdateError(errorMessage);

      // Clear error message after 5 seconds
      setTimeout(() => setEmailUpdateError(''), 5000);
    }
  };


  

  const updatePreferences = (newPreferences) => {
    const accessToken = localStorage.getItem('accessToken');
    const userId = localStorage.getItem('user_id');

    if (!accessToken || !userId) {
      console.log("Missing authentication details");
      return;
    }

    api.post(`/user/user_preferences/${userId}`, newPreferences, {
      headers: {
        'Authorization': `Bearer ${accessToken}`
      }
    })
    .then(response => {
      console.log("Preferences updated successfully");
      // Optionally refresh the state to reflect the changes
    })
    .catch(error => {
      console.error("Error updating preferences:", error);
      // Handle the error
    });
  };

  // Handlers for toggles
  const handleWeeklyReportToggle = (event) => {
    setReceiveWeeklyReport(event.target.checked);
    updatePreferences({ receive_weekly_report: event.target.checked });
  };

  const handleMarketingEmailsToggle = (event) => {
    setAcceptMarketingEmails(event.target.checked);
    updatePreferences({ accept_marketing_emails: event.target.checked });
  };

  const handleDeleteAccount = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deletePassword) {
      alert('Please enter your password to confirm deletion.');
      return;
    }

    setDeleteLoading(true);
    const accessToken = localStorage.getItem('accessToken');

    if (!accessToken) {
      alert('Authentication required. Please log in again.');
      setDeleteLoading(false);
      return;
    }

    try {
      await coreApi.delete('/user/parent/delete_account', {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        },
        data: {
          confirmDelete: true,
          password: deletePassword,
          initiated_via: 'web'
        }
      });

      alert('Account deleted successfully. You will be redirected to the home page.');
      localStorage.clear();
      navigate('/');
    } catch (error) {
      console.error('Error deleting account:', error);
      const errorMessage = error.response?.data?.error || 'Failed to delete account. Please try again.';
      alert(errorMessage);
    } finally {
      setDeleteLoading(false);
      setDeleteDialogOpen(false);
      setDeletePassword('');
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setDeletePassword('');
  };





  return (
    <ThemeProvider theme={defaultTheme}>
    <GlobalStyles styles={{ ul: { margin: 0, padding: 0, listStyle: 'none' } }} />
    <CssBaseline />
    
    {/* AppBar */}
    <AppBar
    position="static"
    color="default"
    elevation={0}
    sx={{ borderBottom: (theme) => `1px solid ${theme.palette.divider}` }}
  >
    <Toolbar sx={{ flexWrap: 'wrap' }}>
      <nav>
        {/* Your navigation items here */}
      </nav>
      <Button onClick={handleContactClick} variant="outlined" sx={{ my: 1, mx: 1.5 }}>
        Contact Us
      </Button>
      <Box flexGrow={1}></Box> {/* This Box pushes everything after it to the right */}
      <UserGreeting />
    </Toolbar>
  </AppBar>
  <Container component="main" maxWidth="sm" sx={{ mb: 4 }}>
        <CssBaseline />
        <Typography variant="h6" gutterBottom>
        My Account
        </Typography>
        {updateSuccess && (
          <Typography color="primary" sx={{ mt: 2 }}>
            Password changed successfully!
          </Typography>
        )}
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                id="password"
                name="password"
                label="New Password"
                type="password"
                variant="outlined"
                fullWidth
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                sx={{ mb: 2 }} // Add a slight gap below the field
              />
              <TextField
                id="confirmPassword"
                name="confirmPassword"
                label="Confirm New Password"
                type="password"
                variant="outlined"
                fullWidth
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                sx={{ mb: 2 }} // Add a slight gap below the field
              />
              <Button type="submit" variant="contained" color="primary" sx={{ mt: 2 }}> {/* Add gap above the button */}
                Save New Password
              </Button>
            </Grid>

{/* Email Change Section */}
<Grid item xs={12} sx={{ mt: 4 }}>
  <Typography variant="h6" gutterBottom>
    Change Email Address
  </Typography>
</Grid>

{emailUpdateSuccess && (
  <Grid item xs={12}>
    <Typography color="primary">
      Email address updated successfully!
    </Typography>
  </Grid>
)}

{emailUpdateError && (
  <Grid item xs={12}>
    <Typography color="error">
      {emailUpdateError}
    </Typography>
  </Grid>
)}

<Grid item xs={12}>
  <TextField
    id="newEmail"
    name="newEmail"
    label="New Email Address"
    type="email"
    variant="outlined"
    fullWidth
    value={newEmail}
    onChange={e => setNewEmail(e.target.value)}
    sx={{ mb: 2 }}
  />
  <TextField
    id="emailPassword"
    name="emailPassword"
    label="Current Password (to confirm)"
    type="password"
    variant="outlined"
    fullWidth
    value={emailPassword}
    onChange={e => setEmailPassword(e.target.value)}
    sx={{ mb: 2 }}
  />
  <Button
    onClick={handleEmailUpdate}
    variant="contained"
    color="primary"
    sx={{ mt: 2 }}
  >
    Update Email Address
  </Button>
</Grid>

{/* Before "Email Preferences", ensure proper spacing */}
<Grid item xs={12} sx={{ mt: 4 }}> {/* Adjust 'mt' value as needed for more space */}
  <Typography variant="h6" gutterBottom>
    Email Preferences
  </Typography>
</Grid>

{/* Email Preferences Options */}
<Grid item xs={12}>
  <FormControlLabel
    control={<Switch checked={receiveWeeklyReport} onChange={handleWeeklyReportToggle} />}
    label="Receive Weekly Report"
  />
</Grid>
<Grid item xs={12}>
  <FormControlLabel
    control={<Switch checked={acceptMarketingEmails} onChange={handleMarketingEmailsToggle} />}
    label="Accept Marketing Emails"
  />
</Grid>

{/* Delete Account Section */}
<Grid item xs={12} sx={{ mt: 6 }}>
  <Typography variant="h6" gutterBottom color="error">
    Danger Zone
  </Typography>
  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
    Permanently delete your account and all associated data. This action cannot be undone.
  </Typography>
  <Button 
    variant="outlined" 
    color="error" 
    onClick={handleDeleteAccount}
    sx={{ mt: 1 }}
  >
    Delete Account
  </Button>
</Grid>
          </Grid>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
          </Box>
        </form>

        {/* Delete Account Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel} maxWidth="sm" fullWidth>
          <DialogTitle>Delete Account</DialogTitle>
          <DialogContent>
            <Typography variant="body1" sx={{ mb: 3 }}>
              Are you sure you want to delete your account? This will permanently delete:
            </Typography>
            <Typography variant="body2" component="ul" sx={{ mb: 3, pl: 2 }}>
              <li>Your parent account</li>
              <li>All child accounts</li>
              <li>All progress and achievements</li>
              <li>All subscription data</li>
              <li>All associated data</li>
            </Typography>
            <Typography variant="body1" color="error" sx={{ mb: 3, fontWeight: 'bold' }}>
              This action cannot be undone.
            </Typography>
            <TextField
              fullWidth
              type="password"
              label="Enter your password to confirm"
              value={deletePassword}
              onChange={(e) => setDeletePassword(e.target.value)}
              margin="normal"
              required
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDeleteCancel}>Cancel</Button>
            <Button 
              onClick={handleDeleteConfirm} 
              color="error" 
              variant="contained"
              disabled={deleteLoading || !deletePassword}
            >
              {deleteLoading ? 'Deleting...' : 'Delete Account'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </ThemeProvider>
  );
};

export default MyAccount;